import { Container } from '@acies/core';
import { Tag } from '@acies/domain';
import Label from './label';
import TagRendererEvent from './tag-renderer-event';
import theme from './theme';
import XCloseButton from './x-close-button';
import XCloseButtonEvent from './x-close-button-event';

/** The TagRenderer component is a closeable Tag rendering UI widget with an optinal close button. */
export default class TagRenderer extends Container {
	public static override observedAttributes: string[] = [...Container.observedAttributes, 'closeable'];
	/** The closeable property, toggles the visibility of the x close button to the right, so closeable set to true, will show the button and false will hide it. */
	public set closeable(value: boolean) {
		if (this.#closeable === value) {
			return;
		}
		assertIsBoolean(value);
		this.#closeable = value;
		this.#closeableChanged = true;
		this.invalidateProperties();
	}

	public get closeable(): boolean {
		return this.#closeable;
	}

	/** An instance of the Tag domain class. */
	public set data(value: null | Tag) {
		if (this.#data === value) {
			return;
		}
		assertIsValidData(value);
		this.#data = value;
		this.#dataChanged = true;
		this.invalidateProperties();
	}

	public get data(): null | Tag {
		return this.#data;
	}

	#closeable: boolean;
	#closeableChanged: boolean;
	#closeButton!: XCloseButton;
	#data: null | Tag;
	#dataChanged: boolean;
	#keyChildContainer!: Container;
	#keyLabel!: Label;
	#valueChildContainer!: Container;
	#valueLabel!: Label;

	private get closeButton(): XCloseButton {
		if (!this.#closeButton) {
			this.#closeButton = new XCloseButton();
			this.#closeButton.visible = false;
		}
		return this.#closeButton;
	}

	private get keyChildContainer(): Container {
		if (!this.#keyChildContainer) {
			this.#keyChildContainer = new Container();
			this.#keyChildContainer.alignment = 'center';
			this.#keyChildContainer.cornerRadius = [6, 0, 0, 6];
			this.#keyChildContainer.fill = theme.colors.fill.white;
			this.#keyChildContainer.height = 'fill';
			this.#keyChildContainer.paddingHorizontal = 6;
			this.#keyChildContainer.stroke = theme.strokes.soft;
			this.#keyChildContainer.addComponent(this.keyLabel);
		}
		return this.#keyChildContainer;
	}

	private get keyLabel(): Label {
		if (!this.#keyLabel) {
			this.#keyLabel = new Label();
			this.#keyLabel.size = 'xsmall';
		}
		return this.#keyLabel;
	}

	private get valueChildContainer(): Container {
		if (!this.#valueChildContainer) {
			this.#valueChildContainer = new Container();
			this.#valueChildContainer.alignment = 'center';
			this.#valueChildContainer.cornerRadius = [0, 6, 6, 0];
			this.#valueChildContainer.fill = theme.colors.fill.white;
			this.#valueChildContainer.height = 'fill';
			this.#valueChildContainer.paddingHorizontal = [6, 6];
			this.#valueChildContainer.gapHorizontal = 2;
			this.#valueChildContainer.stroke = theme.strokes.soft;
			this.#valueChildContainer.addComponents([this.valueLabel, this.closeButton]);
		}
		return this.#valueChildContainer;
	}

	private get valueLabel(): Label {
		if (!this.#valueLabel) {
			this.#valueLabel = new Label();
			this.#valueLabel.size = 'xsmall';
		}
		return this.#valueLabel;
	}

	public constructor() {
		super();
		this.height = 20;
		this.minWidth = 40;
		this.#closeable = false;
		this.#closeableChanged = false;
		this.#data = null;
		this.#dataChanged = false;
	}

	protected override attributeChangedCallback(name: string, _: string, value: string) {
		super.attributeChangedCallback(name, _, value);
		if (name === 'closeable') {
			this.#closeableAttributeChanged(value);
		}
	}

	protected override commitProperties(): void {
		super.commitProperties();
		if (this.#closeableChanged) {
			this.#commitCloseable();
		}
		if (this.#dataChanged) {
			this.#commitData();
		}
	}

	protected override initialize(): void {
		super.initialize();
		this.addComponents([this.keyChildContainer, this.valueChildContainer]);
		this.addEventListener(XCloseButtonEvent.CLOSE, this.#close);
	}

	#close(event: XCloseButtonEvent): void {
		event.stopImmediatePropagation();
		if (this.#data) {
			this.dispatchEvent(new TagRendererEvent(this.#data));
		}
	}

	#closeableAttributeChanged(value: string): void {
		if (value === 'true') {
			this.closeable = true;
		} else {
			this.closeable = false;
		}
	}

	#commitCloseable(): void {
		this.#closeableChanged = false;
		if (this.#closeable) {
			this.closeButton.visible = true;
			this.valueChildContainer.paddingHorizontal = [6, 2];
			this.addEventListener(XCloseButtonEvent.CLOSE, this.#close);
		} else {
			this.closeButton.visible = false;
			this.valueChildContainer.paddingHorizontal = [6, 6];
			this.removeEventListener(XCloseButtonEvent.CLOSE, this.#close);
		}
	}

	#commitData(): void {
		this.#dataChanged = false;
		if (this.#data) {
			this.keyLabel.text = this.#data.key.value;
			this.valueLabel.text = this.#data.value.value;
		} else {
			this.keyLabel.text = '';
			this.valueLabel.text = '';
		}
	}
}
customElements.define('ac-tag-renderer', TagRenderer);

function assertIsBoolean(value: unknown): asserts value is boolean {
	if (typeof value === 'boolean') {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be true or false`);
}

function assertIsValidData(value: unknown): asserts value is null | Tag {
	if (value === null || value instanceof Tag) {
		return;
	}
	throw new TypeError(`[${String(value)}] is invalid, must be null or instance of Tag`);
}
